import { Document as CtfDocument } from "@contentful/rich-text-types";
import { css } from "@emotion/react";
import type { ExpandedRichText } from "@swo/shared";
import React, { useRef } from "react";
import isEqual from "react-fast-compare";
import useMeasure from "react-use-measure";
import { calculateRichTextLength } from "../../../../components/_shared-components/so-rich-text";
import { CSS_MQ } from "../../../../styles/breakpoints";
import { NavigationBar } from "./NavigationBar";
import { useNavigation } from "./useNavigation";

export function CardsCarousel<T>({
  items,
  Component,
  findText,
  getKey,
  theme,
}: {
  items: Array<T>;
  Component: React.ComponentType<{ item: T; isActive?: boolean; ref?: (element: HTMLOrSVGElement) => void }>;
  findText: ((t: T) => Array<ExpandedRichText>) | null;
  getKey: (t: T) => string;
  theme: "dark-background" | "light-background";
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { handlers, onPrevious, onNext, visibleIdx, setVisibleIdx, shouldPassThroughWalls } = useNavigation(items, ref);

  const [refForBounds, bounds] = useMeasure();

  function getItemWithLongestText() {
    if (!findText) {
      return null;
    }

    const texts = items.flatMap(e => findText(e).map(_ => _.json));
    const optionsLengths = texts.map(json => calculateRichTextLength((json as CtfDocument).content));

    const longestOption = Math.max(...optionsLengths);
    const longestOptionIdx = optionsLengths.findIndex(o => o === longestOption);

    const longestText = texts[longestOptionIdx];

    return items.find(i => findText(i).some(t => isEqual(t.json, longestText)));
  }

  const itemWithLongestText = getItemWithLongestText();

  return (
    <>
      <div {...handlers}>
        <div
          ref={ref}
          css={css`
            width: 100%;
            ${CSS_MQ.atLeastMd} {
              width: 80%;
            }

            margin: 0 auto;

            position: relative;
            transition: 0.3s ease-out;
          `}
          style={{
            transform: `translateX(calc(${-100 * visibleIdx}% - ${visibleIdx * 40}px))`,
            minHeight: `${bounds.height}px`,
          }}
        >
          {itemWithLongestText ? (
            <div
              className={"placeholder"}
              css={css`
                width: 100%;
                height: 100%;
                z-index: -1;

                visibility: hidden;
              `}
            >
              <Component item={itemWithLongestText} />
            </div>
          ) : null}

          {items.map((el, idx) => {
            const style = {
              transform: `translateX(calc(${100 * idx}% + ${idx} * var(--g)))`,
              display: "block",
            };

            const isActive = idx === visibleIdx;
            const isClickable = items.length > 1 && !isActive;

            return (
              <div
                tabIndex={0}
                onClick={isClickable ? () => setVisibleIdx(idx) : undefined}
                key={getKey(el)}
                css={cardWrapper}
                style={style}
              >
                <Component item={el} isActive={isActive} ref={refForBounds as any} />
              </div>
            );
          })}
        </div>
      </div>

      {items.length > 1 && (
        <NavigationBar
          theme={theme}
          onPrevious={onPrevious}
          onNext={onNext}
          shouldPassThroughWalls={shouldPassThroughWalls}
          visibleIdx={visibleIdx}
          setVisibleIdx={setVisibleIdx}
          items={items}
        />
      )}
    </>
  );
}

const cardWrapper = css`
  cursor: pointer;
  position: absolute;

  --g: 40px;

  width: 100%;
  height: 100%;

  top: 0;
  left: 0;

  z-index: 1;
`;
